import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { PaylinkStatus, currencyUtils } from 'skipify-types';

import { CallSplit } from '@mui/icons-material';
import { formatCurrency } from '../../../../util/formatters';
import { DeliveryMethods } from '../DeliveryMethods';
import { getStatusUi } from '../../constants';
import { PaylinkStatusChip } from '../StatusChip/PaylinkStatusChip';
import { PartialPaymentAllowedPill } from './PartialPaymentAllowedPill';
import { PaylinkSummaryAmountSection } from './PaylinkSummaryAmountSection';

interface Props {
  status: PaylinkStatus;
  approvedAmount: number | null;
  orderTotal: number;
  sendEmail: boolean;
  sendSms: boolean;
  size?: 'small' | 'medium';
  multiplePayments?: boolean;
  enablePartialPayment?: boolean | null;
  currencyCode?: string;
}

export const PaylinkSummary = React.memo(
  ({
    status,
    approvedAmount,
    orderTotal,
    sendEmail,
    sendSms,
    size = 'medium',
    multiplePayments,
    enablePartialPayment,
    currencyCode,
  }: Props) => {
    const showPartialPaymentAllowed =
      !!enablePartialPayment &&
      (status === 'CREATED' || status === 'SENT' || status === 'OPENED' || status === 'FAILED');

    const partialPayment =
      approvedAmount != null &&
      approvedAmount < orderTotal &&
      (status === 'PARTIALLY_PAID' || (status === 'PAID' && !!enablePartialPayment))
        ? { paidAmountMinor: approvedAmount, totalAmountMinor: orderTotal }
        : null;
    const displayAmount = formatCurrency(
      currencyUtils.minorToMajor(partialPayment?.totalAmountMinor ?? approvedAmount ?? orderTotal),
      currencyCode,
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: partialPayment ? 'stretch' : 'center',
          padding: size === 'small' ? 1.5 : 2.5,
          borderRadius: '24px',
          backgroundColor: getStatusUi(status).color.bg,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <PaylinkStatusChip status={status} size="medium" />
        </Box>
        {multiplePayments && (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ alignSelf: 'center' }}>
            <CallSplit sx={{ fontSize: 14 }} />
            <Typography>Multiple Payments</Typography>
          </Stack>
        )}
        {showPartialPaymentAllowed && (
          <Box sx={{ alignSelf: 'center' }}>
            <PartialPaymentAllowedPill />
          </Box>
        )}
        <PaylinkSummaryAmountSection
          partialPayment={partialPayment}
          displayAmount={displayAmount}
          approvedAmount={approvedAmount}
          orderTotal={orderTotal}
          currencyCode={currencyCode}
          size={size}
        />
        <Box
          sx={{
            marginTop: size === 'small' ? 1 : 1.5,
            alignSelf: 'center',
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <DeliveryMethods sendEmail={sendEmail} sendSms={sendSms} />
        </Box>
      </Box>
    );
  },
);

PaylinkSummary.displayName = 'PaylinkSummary';
